import {
  getNestedSubelementsByType,
  getMostRecentFieldValue,
  getSlsTypeByValue,
  getPersonName,
  getNestedOrFloatingElementById,
  getOtherPersonId,
  getRelationshipLabel,
} from '@fs/zion-sls'
import { getFieldValueText } from '../../panels/highlightPanel/utils/highlightUtils'

/**
 * Retrieves the original primary text value from defaultValues for a specific field path.
 * Helper function to reduce duplication when accessing deeply nested form default values.
 *
 * @param {Object} defaultValues - The form's default values object.
 * @param {boolean} isPrimary - Whether this is a primary event (true) or additional event (false).
 * @param {number} eventIndex - Index of the event in the events array.
 * @param {string} fieldPath - The field path (e.g., 'dates', 'places', 'other').
 * @param {number} itemIndex - Index of the item in the field array.
 * @returns {string|undefined} The original primaryText value or undefined if not found.
 */
export const getOriginalFormValue = (defaultValues, isPrimary, eventIndex, fieldPath, itemIndex) => {
  const eventType = isPrimary ? 'primary' : 'additional'
  const basePath = defaultValues?.events?.[eventType]?.[eventIndex]?.[fieldPath]?.[itemIndex]

  return basePath?.value?.primaryText || basePath?.value
}

/**
 * Validates if a linked event field has changed and returns a warning message if needed.
 * Used for form field validation to warn users when editing linked events.
 *
 * @param {boolean} isLinkedEvent - Whether this is a linked event affecting multiple people.
 * @param {*} currentValue - The current value being validated.
 * @param {*} originalValue - The original value from defaultValues.
 * @param {function} t - Translation function (from useTranslation hook).
 * @returns {string|null} Warning message if value changed on a linked event, otherwise null.
 */
export const validateLinkedEventFieldChange = (isLinkedEvent, currentValue, originalValue, t) => {
  if (isLinkedEvent && currentValue !== originalValue) {
    return t('edit-events.linked-field-change-warning', 'Changes will be made on all who are linked.')
  }
  return null
}

/**
 * Retrieves all EVENT subelements directly under a person, including those related to relationships.
 * For events originating from relationships, attaches relationship data to the event.
 *
 * @param {Object} person - The person object.
 * @returns {Array<Object>} Array of event objects, each with optional relationship data.
 */
export const getEventsFromPerson = (person) => {
  if (!person) return []

  // This will get all EVENT subelements directly under the person even if they are related to relationships
  const personEvents = getNestedSubelementsByType(person, 'EVENT') || []

  // Map event IDs to their corresponding relationship data for quick lookup
  const personRelationships = getNestedSubelementsByType(person, 'RELATIONSHIP') || []
  const eventsMap = new Map()
  personRelationships.forEach((relationship) => {
    const event = relationship?.subElements?.find((subEl) => subEl.elementType === 'EVENT')
    if (event?.id) {
      // Store the relationship data for this event ID
      eventsMap.set(event.id, relationship)
    }
  })

  // Attach relationship data to events that originate from relationships
  const processedPersonEvents = personEvents.map((event) => ({
    ...event,
    fromRelationship: eventsMap.has(event.id),
    relationshipData: eventsMap.get(event.id),
  }))

  return processedPersonEvents
}

/**
 * Sort function that moves an object (sls event) with primary=true to the top of a list.
 * @param {Object} a - Current object being compared.
 * @param {Object} b - Next object being compared.
 * @returns {number}
 */
export function sortPrimaryToTop(a, b) {
  const aPrimary = a?.primary ? 1 : 0
  const bPrimary = b?.primary ? 1 : 0
  if (bPrimary !== aPrimary) return bPrimary - aPrimary
  return 0
}

/**
 * Retrieves the primary event from a person's record.
 * The primary event is an event with a primaryOnly type found on the record element.
 *
 * @param {Object} person - The person object.
 * @param {Array} images - Array of images from SLS state context.
 * @returns {Object|null} The record's primary event or null if not found.
 */
export function getRecordPrimaryEvent(person, images) {
  if (!person?.recordId) return null

  const record = getNestedOrFloatingElementById(images, person.recordId)
  if (!record) return null

  const recordEvents = getNestedSubelementsByType(record, 'EVENT') || []
  return recordEvents.find((ev) => ev.primary)
}

/**
 * Helper function to get field IDs from an event object.
 * @private
 */
function getFieldIdsFromEventObject(event) {
  const dateFieldIds = event.dates?.map((date) => date.id) || []
  const placeFieldIds = event.places?.map((place) => place.id) || []
  const otherFieldIds = event.other?.map((other) => other.id) || []
  return [...dateFieldIds, ...placeFieldIds, ...otherFieldIds].filter(Boolean)
}

/**
 * Extracts all field IDs from form values for the new simplified event structure.
 *
 * @param {Object} formValues - Form values containing events.primary and events.additional arrays
 * @returns {Array<string>} Array of all field IDs
 */
export function getFieldIdsFromFormValues(formValues) {
  const primaryFieldIds = formValues?.events?.primary?.flatMap(getFieldIdsFromEventObject) || []
  const additionalFieldIds = formValues?.events?.additional?.flatMap(getFieldIdsFromEventObject) || []
  return [...primaryFieldIds, ...additionalFieldIds]
}

/**
 * Retrieves the most recent event type field value from an event object.
 *
 * @param {Object} event - The event object containing subElements.
 * @param {Object} [slsTypes] - Optional SLS types object containing events array for fallback lookup.
 * @returns {any} The most recent field value for the event type, or undefined if not found.
 */
export function getEventTypeFieldValue(event, slsTypes) {
  // First try to get event type from the event's eventType property
  const { eventType } = event
  const eventSlsType = getSlsTypeByValue(eventType, 'events', slsTypes)

  if (eventSlsType) {
    return eventSlsType?.displayLabel || eventType?.label
  }

  // Fall back to looking for an EVENT_TYPE field directly under the event
  const eventTypeField = getNestedSubelementsByType(event, 'EVENT_TYPE')[0]
  const eventTypeFieldValue = getMostRecentFieldValue(eventTypeField)

  if (eventTypeFieldValue) {
    return eventTypeFieldValue
  }

  // Return empty string if neither source has the event type
  return ''
}

/**
 * Maps an array of elements/fields to formatted field values and filters out empty values.
 * @private
 * @param {Array} items - Array of elements or fields to map
 * @param {boolean} [allowRawValue=false] - Whether to use raw value instead of most recent field value from the getMostRecentFieldValue function
 * @returns {Array<{id: string, displayLabel: string, value: string}>} Formatted field values with non-empty values
 */
function mapToFieldValues(items, allowRawValue = false) {
  return items
    .map((item) => ({
      id: item.id,
      displayLabel: item.displayLabel,
      value: allowRawValue ? item.value : getMostRecentFieldValue(item),
    }))
    .filter((item) => item.value && item.value.trim() !== '')
}

/**
 * Gets relationship field values for display.
 * @private
 */
const getRelationshipFieldValues = ({ relationship, images, person }) => {
  if (!relationship?.id) return { label: '', name: '' }

  const otherPersonId = getOtherPersonId(relationship, person)
  const otherPerson = getNestedOrFloatingElementById(images, otherPersonId)
  const order = relationship?.superElements?.find((supEl) => supEl.id === otherPersonId)?.order
  const otherPersonName = getPersonName({ person: otherPerson })
  const typeData = relationship?.typeData

  const displayLabel =
    (otherPerson &&
      order &&
      typeData &&
      getRelationshipLabel({
        otherPerson,
        order,
        typeData: relationship?.typeData,
      })) ||
    ''

  return { id: relationship?.id, displayLabel, value: otherPersonName }
}

/**
 * Extracts and formats field values from event elements for display.
 *
 * For primary events, returns aggregated element values (e.g., "Date: 1920").
 * For non-primary events, returns individual field values (e.g., "Day: 15", "Month: May", "Year: 1920").
 *
 * @param {Object} event - The event object containing subElements
 * @param {string} elementType - Type of element to extract ('DATE' or 'PLACE')
 * @param {Object} [images] - Optional images object for relationship context
 * @param {Object} [person] - Optional person object for relationship context
 * @returns {Array<{id: string, displayLabel: string, value: string}>} Array of formatted field values with non-empty values
 */
export function getElementFieldValues(event, elementType, images, person) {
  // Get elements of specified type (DATE or PLACE) - using zion-sls utility for recursive search
  const elements = getNestedSubelementsByType(event, elementType)

  // For relationship events, return relationship related data
  if (event?.relationshipData && images && person) {
    const relationshipFieldValues = getRelationshipFieldValues({ relationship: event.relationshipData, images, person })
    return mapToFieldValues([relationshipFieldValues], true)
  }

  // Return all individual field values
  const fields = elements.flatMap((element) => element.subElements?.filter((el) => el.elementType === 'FIELD') || [])

  return mapToFieldValues(fields)
}

/**
 * Gets event summary from field for display (date | place format).
 *
 * @param {Object} field - Event field object with dates and places arrays
 * @param {string} [separator=' | '] - Separator between date and place
 * @returns {string|null} Formatted summary or null if no data
 */
export const getEventSummaryFromField = (field, separator = ' | ') => {
  const dateField = field?.dates?.find((d) => d?.type === 'DATE')
  const placeField = field?.places?.find((p) => p?.type === 'PLACE')

  if (!dateField && !placeField) return null

  const dateValue = getFieldValueText(dateField)
  const placeValue = getFieldValueText(placeField)

  return [dateValue, placeValue].filter(Boolean).join(separator)
}
